import { useEffect, useState, type ComponentType } from 'react';
import { Building2, Coins, FileCheck2, LayoutGrid, MailWarning, Settings, SlidersHorizontal, Users, Workflow, X } from 'lucide-react';
import type { AcessoConfiguracoesDto, SecaoConfiguracao } from '@nexital/shared';
import { useDependencias, useEnvio } from '../../application/dependencias';
import { SecaoDocumentos } from './SecaoDocumentos';
import { SecaoEmpresa } from './SecaoEmpresa';
import { SecaoEnvios } from './SecaoEnvios';
import { SecaoEspacos } from './SecaoEspacos';
import { SecaoEstados } from './SecaoEstados';
import { SecaoOrcamentoIa } from './SecaoOrcamentoIa';
import { SecaoPadroes } from './SecaoPadroes';
import { SecaoUsuarios } from './SecaoUsuarios';
import './configuracoes.css';

type Secao = { rotulo: string; descricao: string; Icone: ComponentType<{ size?: number }>; Conteudo: ComponentType<{ acesso: AcessoConfiguracoesDto }> };

const SECOES: Record<SecaoConfiguracao, Secao> = {
  empresa: { rotulo: 'Empresa', descricao: 'Cadastro, endereço, representante legal e dados bancários', Icone: Building2, Conteudo: SecaoEmpresa },
  documentos: { rotulo: 'Documentos', descricao: 'Habilitação da empresa, com validade e avisos', Icone: FileCheck2, Conteudo: SecaoDocumentos },
  usuarios: { rotulo: 'Usuários', descricao: 'Quem está na empresa, perfis, gestores e bloqueio', Icone: Users, Conteudo: SecaoUsuarios },
  espacos: { rotulo: 'Espaços', descricao: 'Quadros da empresa: criar, renomear e arquivar', Icone: LayoutGrid, Conteudo: SecaoEspacos },
  estados: { rotulo: 'Estados', descricao: 'Etapas de cada quadro, cores, ordem e motivos', Icone: Workflow, Conteudo: SecaoEstados },
  padroes: { rotulo: 'Padrões do sistema', descricao: 'Limites, prazos, avisos, inatividade e chat', Icone: SlidersHorizontal, Conteudo: SecaoPadroes },
  envios: { rotulo: 'Envios', descricao: 'E-mails e mensagens que não foram enviados: motivo, detalhe e reenvio', Icone: MailWarning, Conteudo: SecaoEnvios },
  'orcamento-ia': { rotulo: 'Orçamento de IA', descricao: 'Gasto do mês com IA: da empresa e do sistema', Icone: Coins, Conteudo: SecaoOrcamentoIa },
};

/** Início das Configurações: um cartão por seção que a pessoa pode abrir. */
function Inicio({ acesso, abrir }: { acesso: AcessoConfiguracoesDto; abrir: (s: SecaoConfiguracao) => void }) {
  return (
    <div className="config__inicio">
      {acesso.secoes.map((s) => {
        const { rotulo, descricao, Icone } = SECOES[s];
        return (
          <button key={s} type="button" className="config__cartao" onClick={() => abrir(s)}>
            <span className="config__cartao-icone">
              <Icone size={22} />
            </span>
            <strong>{rotulo}</strong>
            <span>{descricao}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Configurações da empresa e do sistema (ADR-0022), sobre o quadro como a licitação: fundo escuro, o que está atrás
 * bloqueado. Só aparecem as seções do papel da pessoa na empresa.
 */
export function TelaConfiguracoes() {
  const { configuracoes } = useDependencias();
  const secao = useEnvio((s) => s.configuracoes);
  const abrir = useEnvio((s) => s.abrirConfiguracoes);
  const [acesso, definirAcesso] = useState<AcessoConfiguracoesDto | null>(null);
  const [erro, definirErro] = useState<string>();
  useEffect(() => {
    configuracoes.acesso().then(definirAcesso, (e: Error) => definirErro(e.message));
  }, [configuracoes]);
  if (!secao) return null;
  const atual = secao !== 'inicio' && acesso?.secoes.includes(secao) ? SECOES[secao] : null;

  return (
    <div className="config-fundo" onKeyDown={(e) => e.key === 'Escape' && abrir(null)}>
      <section className="config" role="dialog" aria-modal="true" aria-label="Configurações">
        <Menu secoes={acesso?.secoes ?? []} atual={secao} abrir={abrir} />
        <div className="config__conteudo">
          <Cabecalho atual={atual} fechar={() => abrir(null)} />
          {erro && <p className="cartao-acesso__erro">{erro}</p>}
          {acesso && (atual ? <atual.Conteudo acesso={acesso} /> : <Inicio acesso={acesso} abrir={abrir} />)}
        </div>
      </section>
    </div>
  );
}

function Cabecalho({ atual, fechar }: { atual: Secao | null; fechar: () => void }) {
  return (
    <header className="config__cabecalho">
      <h1>{atual?.rotulo ?? 'Configurações'}</h1>
      {atual && <p>{atual.descricao}</p>}
      <button type="button" className="botao-icone config__fechar" aria-label="Fechar as configurações" onClick={fechar}>
        <X size={20} />
      </button>
    </header>
  );
}

const classe = (ativo: boolean) => `config__item${ativo ? ' config__item--ativo' : ''}`;

function Menu({ secoes, atual, abrir }: { secoes: SecaoConfiguracao[]; atual: SecaoConfiguracao | 'inicio'; abrir: (s: SecaoConfiguracao | 'inicio') => void }) {
  return (
    <nav className="config__menu" aria-label="Seções das configurações">
      <button type="button" className={classe(atual === 'inicio')} onClick={() => abrir('inicio')}>
        <Settings size={18} aria-hidden /> Configurações
      </button>
      {secoes.map((s) => {
        const { rotulo, Icone } = SECOES[s];
        return (
          <button key={s} type="button" className={classe(atual === s)} aria-current={atual === s} onClick={() => abrir(s)}>
            <Icone size={18} /> {rotulo}
          </button>
        );
      })}
    </nav>
  );
}
